// 채팅에 올라온 이미지(보낸 뒤) 썸네일 + 탭하면 열리는 전체화면 뷰어.
// - IntersectionObserver로 화면에 들어오기 전엔 아무 것도 받아오지 않는다(긴 대화방에서
//   스크롤 위의 이미지까지 전부 fetch하지 않게).
// - 기본은 서버가 내려준 fileUrl을 그대로 쓴다(현재 공개 버킷 URL이라 인증이 필요 없다).
//   혹시 그 URL이 깨지거나(비공개 전환 등) 인증이 필요해지는 경우를 대비해, 로드 실패 시
//   인증된 다운로드 API(JWT 헤더 + blob)로 한 번 더 시도한 뒤에만 "불러올 수 없음"으로 넘어간다.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Dialog, IconButton, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as fileApi from '../../lib/api/file';

const THUMB_MAX_WIDTH = 220;

const ChatImage = ({ fileUuid, fileUrl, fileName, onDownload, downloading }) => {
  const containerRef = useRef(null);
  const objectUrlRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [triedAuthFallback, setTriedAuthFallback] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (fileUrl) setSrc(fileUrl);
    // 주소가 없으면(봇 업로드 카드 등) 바로 인증 다운로드로 받아 온다 — 없으면 로딩만 돌고 끝났다
    else handleErrorRef.current();
  }, [visible, fileUrl]);

  // 언마운트 시 혹시 만들어둔 object URL을 정리한다(원본 fileUrl을 쓸 땐 만든 적이 없어 no-op).
  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const handleError = useCallback(async () => {
    if (triedAuthFallback || !fileUuid) {
      setFailed(true);
      return;
    }
    setTriedAuthFallback(true);
    try {
      const res = await fileApi.downloadFile(fileUuid);
      const blobUrl = URL.createObjectURL(new Blob([res.data]));
      objectUrlRef.current = blobUrl;
      setSrc(blobUrl);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [fileUuid, triedAuthFallback]);
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  return (
    <Box ref={containerRef}>
      {failed ? (
        <Box sx={{ py: 1 }}>
          <Typography variant="caption" color="text.secondary">이미지를 불러올 수 없습니다</Typography>
        </Box>
      ) : !visible || !src ? (
        <Box sx={{
          width: THUMB_MAX_WIDTH, height: 140, borderRadius: 2, bgcolor: 'action.hover',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {visible && <CircularProgress size={20} />}
        </Box>
      ) : (
        <Box
          component="img"
          src={src}
          alt={fileName || '이미지'}
          loading="lazy"
          onClick={() => setViewerOpen(true)}
          onError={handleError}
          sx={{
            display: 'block',
            maxWidth: THUMB_MAX_WIDTH,
            width: '100%',
            height: 'auto',
            borderRadius: 2,
            objectFit: 'contain',
            cursor: 'zoom-in',
          }}
        />
      )}

      <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} maxWidth="md" fullWidth>
        <Box sx={{
          position: 'relative', bgcolor: 'common.black',
          display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240,
        }}>
          <IconButton
            onClick={() => setViewerOpen(false)}
            aria-label="닫기"
            sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
          >
            <CloseIcon />
          </IconButton>
          {onDownload && (
            <IconButton
              onClick={onDownload}
              disabled={downloading}
              aria-label="다운로드"
              sx={{ position: 'absolute', top: 8, right: 56, color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}
            >
              {downloading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <GetAppIcon />}
            </IconButton>
          )}
          {src && (
            <Box
              component="img"
              src={src}
              alt={fileName || '이미지'}
              sx={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default ChatImage;
